import { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Loader2, Trophy, Phone, Mail } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import { useTranslation } from "../i18n";
import { LanguageSelector } from "../components/LanguageSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

const TOTAL_STEPS = 7;

const AGE_GROUPS = ["Under 18", "18\u201324", "25\u201334", "35\u201344", "45+"];
const FOLLOW_FREQUENCY = ["Every match", "Most matches", "Occasionally", "Rarely"];
const SUPPORT_DURATION = ["Less than 1 year", "1\u20133 years", "3\u20135 years", "5\u201310 years", "10+ years"];
const ENGAGEMENT_OPTIONS = [
  "Watch matches (TV/streaming)", "Social media", "Buy merchandise",
  "Fan groups / communities", "Events / watch parties", "Other"
];
const ENGAGEMENT_BARRIERS = [
  "Lack of access to official products", "No membership options",
  "Payment challenges", "Distance/location", "Lack of recognition", "Other"
];
const MEMBERSHIP_VALUES = [
  "Behind-the-scenes access", "Voting on club decisions", "Exclusive content",
  "Access to official merchandise", "Discounts and rewards",
  "Community with other fans", "Recognition by the club"
];
const MONTHLY_PRICES = ["$3", "$4", "$5", "$6", "$7", "$8"];

export default function SurveyPage() {
  const { respondentId } = useParams();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref');
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [clubs, setClubs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [registrationData, setRegistrationData] = useState(null);
  
  const [answers, setAnswers] = useState({
    prize_draw_opt_in: "",
    prize_email: "",
    prize_phone: "",
    country: "",
    city: "",
    age_group: "",
    follow_frequency: "",
    club_supported: "",
    club_other: "",
    support_duration: "",
    current_engagement: [],
    feel_recognised: "",
    clubs_do_enough: "",
    connection_level: "",
    engagement_barriers: [],
    membership_interest: "",
    membership_values: [],
    monthly_price: "",
    club_improvement: ""
  });

  const fetchClubs = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/clubs`);
      const data = await response.json();
      setClubs(data.all || []);
    } catch {
      // club fetch failed silently
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/countries`);
      const data = await response.json();
      setCountries(data.all || []);
    } catch {
      // country fetch failed silently
    }
  }, []);

  useEffect(() => {
    initializeSurvey();
    fetchClubs();
    fetchCountries();
  }, [respondentId, fetchClubs, fetchCountries]);

  const initializeSurvey = async () => {
    try {
      // Get registration data from localStorage (legacy flow)
      const stored = localStorage.getItem("registration");
      if (stored) {
        const regData = JSON.parse(stored);
        setRegistrationData(regData);
        setAnswers(prev => ({
          ...prev,
          prize_email: regData.email || "",
          prize_phone: regData.phone_number || "",
          country: regData.country || "",
          city: regData.city || ""
        }));
      }

      // Start survey session
      const body = respondentId ? { respondent_id: respondentId } : {};
      const response = await fetch(`${API_URL}/api/survey/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error("Failed to start survey");
      }

      const session = await response.json();
      setSessionId(session.session_id);
      setCurrentStep(session.current_step || 1);

      // Load saved progress
      const progressResponse = await fetch(`${API_URL}/api/survey/progress/${session.session_id}`);
      if (progressResponse.ok) {
        const progress = await progressResponse.json();
        if (progress.answers_by_step) {
          const mergedAnswers = { ...answers };
          Object.values(progress.answers_by_step).forEach(stepAnswers => {
            Object.assign(mergedAnswers, stepAnswers);
          });
          setAnswers(mergedAnswers);
        }
      }
    } catch (error) {
      toast.error("Failed to load survey. Please try again.");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const saveStepAnswers = async (step, stepAnswers) => {
    if (!sessionId) return;
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/survey/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, step, answers: stepAnswers })
      });
    } catch (error) {
      toast.error("Failed to save answers");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const stepAnswers = getStepAnswers(currentStep);
    if (!validateStep(currentStep)) return;
    
    await saveStepAnswers(currentStep, stepAnswers);
    
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      await completeSurvey();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const getStepAnswers = (step) => {
    switch (step) {
      case 1: return { prize_draw_opt_in: answers.prize_draw_opt_in, prize_email: answers.prize_email, prize_phone: answers.prize_phone };
      case 2: return { country: answers.country, city: answers.city, age_group: answers.age_group, follow_frequency: answers.follow_frequency };
      case 3: return { club_supported: answers.club_supported, club_other: answers.club_other, support_duration: answers.support_duration };
      case 4: return { current_engagement: answers.current_engagement, feel_recognised: answers.feel_recognised, clubs_do_enough: answers.clubs_do_enough, connection_level: answers.connection_level };
      case 5: return { engagement_barriers: answers.engagement_barriers };
      case 6: return { membership_interest: answers.membership_interest, membership_values: answers.membership_values, monthly_price: answers.monthly_price };
      case 7: return { club_improvement: answers.club_improvement };
      default: return {};
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (answers.prize_draw_opt_in === "Yes") {
          if (!answers.prize_email) { toast.error("Please enter your email for prize entry"); return false; }
          if (!answers.prize_phone) { toast.error("Please enter your phone number for prize entry"); return false; }
        }
        return true;
      case 2:
        if (!answers.country) { toast.error("Please select your country"); return false; }
        if (!answers.age_group) { toast.error("Please select your age group"); return false; }
        if (!answers.follow_frequency) { toast.error("Please indicate how often you follow your club"); return false; }
        return true;
      case 3:
        if (!answers.club_supported) { toast.error("Please select the club you support"); return false; }
        if (answers.club_supported === "Other" && !answers.club_other) { toast.error("Please specify which club you support"); return false; }
        if (!answers.support_duration) { toast.error("Please indicate how long you've supported this club"); return false; }
        return true;
      case 4:
        if (answers.current_engagement.length === 0) { toast.error("Please select at least one engagement method"); return false; }
        if (!answers.feel_recognised) { toast.error("Please indicate if you feel recognised"); return false; }
        if (!answers.clubs_do_enough) { toast.error("Please indicate if clubs do enough"); return false; }
        if (!answers.connection_level) { toast.error("Please indicate your connection level"); return false; }
        return true;
      case 5:
        if (answers.engagement_barriers.length === 0) { toast.error("Please select at least one barrier"); return false; }
        return true;
      case 6:
        if (!answers.membership_interest) { toast.error("Please indicate your membership interest"); return false; }
        if (answers.membership_values.length === 0) { toast.error("Please select at least one membership value"); return false; }
        if (!answers.monthly_price) { toast.error("Please select a price point"); return false; }
        return true;
      case 7: return true;
      default: return true;
    }
  };

  const completeSurvey = async () => {
    setSaving(true);
    try {
      await saveStepAnswers(currentStep, getStepAnswers(currentStep));
      
      const response = await fetch(`${API_URL}/api/survey/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          session_id: sessionId,
          prize_draw_opt_in: answers.prize_draw_opt_in === "Yes",
          prize_email: answers.prize_email || null,
          prize_phone: answers.prize_phone || null
        })
      });

      if (!response.ok) throw new Error("Failed to complete survey");
      const result = await response.json();
      
      // All users go to the Fan Pass confirmation page
      navigate("/fan-pass", { 
        state: { 
          prizeEligible: result.prize_eligible, 
          club: answers.club_supported, 
          respondentId,
          displayName: result.display_name,
          sessionId
        }
      });
      toast.success("Survey complete! Your forum access is ready.");
    } catch (error) {
      toast.error("Failed to submit survey. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value, maxItems = null) => {
    setAnswers(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      if (maxItems && current.length >= maxItems) {
        toast.error(`You can only select up to ${maxItems} options`);
        return prev;
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const progress = (currentStep / TOTAL_STEPS) * 100;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FBBF24] animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading survey...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              {saving && (
                <span className="text-gray-400 text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t("survey.saving")}
                </span>
              )}
              <span className="text-gray-400 text-sm">
                {t("survey.step")} {currentStep} {t("survey.of")} {TOTAL_STEPS}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="fixed top-16 left-0 right-0 z-40">
        <Progress value={progress} className="h-1 rounded-none bg-[#1C1C1C]" />
      </div>

      {/* Main Content */}
      <main className="pt-24 pb-32 px-4">
        <div className="max-w-2xl mx-auto">
          {currentStep === 1 && (
            <div className="text-center mb-10">
              <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4" data-testid="survey-headline">
                {t("survey.headline")}
              </h1>
              <p className="text-gray-400 text-sm leading-relaxed">
                {t("survey.subtitle")}
              </p>
            </div>
          )}

          <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 rounded-sm">
            {renderStep()}
          </div>

          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentStep === 1}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="survey-prev-button"
            >
              <ArrowLeft className="w-5 h-5" />
              {t("survey.previous")}
            </button>
            
            <button
              onClick={handleNext}
              disabled={saving}
              className="flex items-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-3 hover:bg-[#F59E0B] transition-all disabled:opacity-50"
              data-testid="survey-next-button"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : currentStep === TOTAL_STEPS ? (
                <>
                  {respondentId ? t("survey.submit") : t("survey.completeRegister")}
                  <Check className="w-5 h-5" />
                </>
              ) : (
                <>
                  {t("survey.next")}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>
      </main>
    </div>
  );

  function renderStep() {
    switch (currentStep) {
      case 1: return renderPrizeDrawStep();
      case 2: return renderFanProfileStep();
      case 3: return renderClubSupportStep();
      case 4: return renderEngagementStep();
      case 5: return renderGapStep();
      case 6: return renderFutureStep();
      case 7: return renderVoiceStep();
      default: return null;
    }
  }

  function renderPrizeDrawStep() {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="w-8 h-8 text-[#FBBF24]" />
          <div>
            <h2 className="font-heading text-2xl text-white uppercase">{t("survey.prizeTitle")}</h2>
            <p className="text-gray-400 text-sm">{t("survey.prizeSubtitle")}</p>
          </div>
        </div>
        <div className="bg-[#FBBF24]/10 border border-[#FBBF24]/30 p-4 rounded-sm mb-6">
          <p className="text-[#FBBF24] font-bold text-sm mb-1">{t("survey.winners")}</p>
          <p className="text-gray-300 text-sm">
            {t("survey.prizeInfo")}
          </p>
        </div>
        <div className="space-y-4">
          <Label className="text-white">{t("survey.prizeQ")}</Label>
          <RadioGroup value={answers.prize_draw_opt_in} onValueChange={(value) => handleAnswerChange("prize_draw_opt_in", value)} className="space-y-2">
            <div className="flex items-center space-x-3 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
              <RadioGroupItem value="Yes" id="prize-yes" className="border-white/30 text-[#FBBF24]" />
              <Label htmlFor="prize-yes" className="text-white cursor-pointer flex-1">{t("survey.prizeYes")}</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
              <RadioGroupItem value="No" id="prize-no" className="border-white/30 text-[#FBBF24]" />
              <Label htmlFor="prize-no" className="text-white cursor-pointer flex-1">{t("survey.prizeNo")}</Label>
            </div>
          </RadioGroup>
        </div>
        {answers.prize_draw_opt_in === "Yes" && (
          <div className="space-y-4 pt-4 border-t border-white/10">
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#FBBF24]" />
                Email for Prize Entry <span className="text-red-500">*</span>
              </Label>
              <Input type="email" value={answers.prize_email} onChange={(e) => handleAnswerChange("prize_email", e.target.value)} placeholder="your@email.com" className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] h-12" data-testid="prize-email-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#FBBF24]" />
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input type="tel" value={answers.prize_phone} onChange={(e) => handleAnswerChange("prize_phone", e.target.value)} placeholder="+234 xxx xxx xxxx" className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] h-12" data-testid="prize-phone-input" />
              <p className="text-gray-500 text-xs">Include country code. Required for location verification.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderFanProfileStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">Fan Profile</h2>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.country")} <span className="text-red-500">*</span></Label>
          <Select value={answers.country} onValueChange={(value) => handleAnswerChange("country", value)}>
            <SelectTrigger className="bg-black/50 border-white/20 text-white h-12" data-testid="survey-country-select">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1C] border-white/20 max-h-60">
              {countries.map((country) => (
                <SelectItem key={country} value={country} className="text-white hover:bg-white/10">{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.city")} <span className="text-gray-500">(optional)</span></Label>
          <Input value={answers.city} onChange={(e) => handleAnswerChange("city", e.target.value)} placeholder="Your city" className="bg-black/50 border-white/20 text-white h-12" />
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.ageGroup")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.age_group} onValueChange={(value) => handleAnswerChange("age_group", value)} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {AGE_GROUPS.map((age) => (
              <div key={age} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={age} id={`age-${age}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`age-${age}`} className="text-white cursor-pointer text-sm">{age}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.followFreq")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.follow_frequency} onValueChange={(value) => handleAnswerChange("follow_frequency", value)} className="space-y-2">
            {FOLLOW_FREQUENCY.map((freq) => (
              <div key={freq} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={freq} id={`freq-${freq}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`freq-${freq}`} className="text-white cursor-pointer">{freq}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    );
  }

  function renderClubSupportStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">{t("survey.clubSupport")}</h2>
        <div className="space-y-2">
          <Label className="text-gray-300">Which club do you support? <span className="text-red-500">*</span></Label>
          <Select value={answers.club_supported} onValueChange={(value) => handleAnswerChange("club_supported", value)}>
            <SelectTrigger className="bg-black/50 border-white/20 text-white h-12" data-testid="survey-club-select">
              <SelectValue placeholder="Select your club" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1C] border-white/20 max-h-60">
              {clubs.map((club) => (
                <SelectItem key={club} value={club} className="text-white hover:bg-white/10">{club}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {answers.club_supported === "Other" && (
          <div className="space-y-2">
            <Label className="text-gray-300">{t("survey.clubOther")} <span className="text-red-500">*</span></Label>
            <Input value={answers.club_other} onChange={(e) => handleAnswerChange("club_other", e.target.value)} placeholder="Enter club name" className="bg-black/50 border-white/20 text-white h-12" />
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.duration")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.support_duration} onValueChange={(value) => handleAnswerChange("support_duration", value)} className="space-y-2">
            {SUPPORT_DURATION.map((duration) => (
              <div key={duration} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={duration} id={`dur-${duration}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`dur-${duration}`} className="text-white cursor-pointer">{duration}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    );
  }

  function renderEngagementStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">{t("survey.engagement")}</h2>
        <div className="space-y-3">
          <Label className="text-gray-300">{t("survey.engageQ")} <span className="text-red-500">*</span></Label>
          <p className="text-gray-500 text-sm">{t("survey.selectAll")}</p>
          <div className="grid gap-2">
            {ENGAGEMENT_OPTIONS.map((option) => (
              <div key={option} onClick={() => handleMultiSelect("current_engagement", option)}
                className={`flex items-center space-x-3 p-3 border cursor-pointer transition-colors ${answers.current_engagement.includes(option) ? "bg-[#FBBF24]/10 border-[#FBBF24]/50" : "bg-black/30 border-white/10 hover:border-[#FBBF24]/30"}`}>
                <Checkbox checked={answers.current_engagement.includes(option)} className="border-white/30 data-[state=checked]:bg-[#FBBF24] data-[state=checked]:border-[#FBBF24]" />
                <span className="text-white">{option}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.recognised")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.feel_recognised} onValueChange={(value) => handleAnswerChange("feel_recognised", value)} className="grid grid-cols-3 gap-2">
            {["Yes", "Somewhat", "No"].map((option) => (
              <div key={option} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={option} id={`recog-${option}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`recog-${option}`} className="text-white cursor-pointer">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.doEnough")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.clubs_do_enough} onValueChange={(value) => handleAnswerChange("clubs_do_enough", value)} className="grid grid-cols-3 gap-2">
            {["Yes", "Somewhat", "No"].map((option) => (
              <div key={option} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={option} id={`enough-${option}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`enough-${option}`} className="text-white cursor-pointer">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.connection")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.connection_level} onValueChange={(value) => handleAnswerChange("connection_level", value)} className="space-y-2">
            {["Very connected", "Somewhat connected", "Not connected"].map((option) => (
              <div key={option} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={option} id={`conn-${option}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`conn-${option}`} className="text-white cursor-pointer">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    );
  }

  function renderGapStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">{t("survey.gap")}</h2>
        <div className="space-y-3">
          <Label className="text-gray-300">{t("survey.gapQ")} <span className="text-red-500">*</span></Label>
          <p className="text-gray-500 text-sm">{t("survey.selectAll")}</p>
          <div className="grid gap-2">
            {ENGAGEMENT_BARRIERS.map((barrier) => (
              <div key={barrier} onClick={() => handleMultiSelect("engagement_barriers", barrier)}
                className={`flex items-center space-x-3 p-3 border cursor-pointer transition-colors ${answers.engagement_barriers.includes(barrier) ? "bg-[#FBBF24]/10 border-[#FBBF24]/50" : "bg-black/30 border-white/10 hover:border-[#FBBF24]/30"}`}>
                <Checkbox checked={answers.engagement_barriers.includes(barrier)} className="border-white/30 data-[state=checked]:bg-[#FBBF24] data-[state=checked]:border-[#FBBF24]" />
                <span className="text-white">{barrier}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderFutureStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">Future Opportunity</h2>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.memberQ")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.membership_interest} onValueChange={(value) => handleAnswerChange("membership_interest", value)} className="grid grid-cols-3 gap-2">
            {["Yes", "Maybe", "No"].map((option) => (
              <div key={option} className="flex items-center space-x-2 p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={option} id={`member-${option}`} className="border-white/30 text-[#FBBF24]" />
                <Label htmlFor={`member-${option}`} className="text-white cursor-pointer">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
        <div className="space-y-3">
          <Label className="text-gray-300">{t("survey.memberValues")} <span className="text-red-500">*</span></Label>
          <p className="text-gray-500 text-sm">{t("survey.selectUp")}</p>
          <div className="grid gap-2">
            {MEMBERSHIP_VALUES.map((value) => (
              <div key={value} onClick={() => handleMultiSelect("membership_values", value, 3)}
                className={`flex items-center space-x-3 p-3 border cursor-pointer transition-colors ${answers.membership_values.includes(value) ? "bg-[#FBBF24]/10 border-[#FBBF24]/50" : "bg-black/30 border-white/10 hover:border-[#FBBF24]/30"}`}>
                <Checkbox checked={answers.membership_values.includes(value)} className="border-white/30 data-[state=checked]:bg-[#FBBF24] data-[state=checked]:border-[#FBBF24]" />
                <span className="text-white">{value}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-500 text-sm">{answers.membership_values.length}/3 {t("survey.selected")}</p>
        </div>
        <div className="space-y-2">
          <Label className="text-gray-300">{t("survey.priceQ")} <span className="text-red-500">*</span></Label>
          <RadioGroup value={answers.monthly_price} onValueChange={(value) => handleAnswerChange("monthly_price", value)} className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {MONTHLY_PRICES.map((price) => (
              <div key={price} className="flex items-center justify-center p-3 bg-black/30 border border-white/10 hover:border-[#FBBF24]/50 transition-colors cursor-pointer">
                <RadioGroupItem value={price} id={`price-${price}`} className="sr-only" />
                <Label htmlFor={`price-${price}`} className={`cursor-pointer font-bold ${answers.monthly_price === price ? "text-[#FBBF24]" : "text-white"}`}>{price}</Label>
              </div>
            ))}
          </RadioGroup>
        </div>
      </div>
    );
  }

  function renderVoiceStep() {
    return (
      <div className="space-y-6">
        <h2 className="font-heading text-2xl text-white uppercase mb-6">{t("survey.voice")}</h2>
        <p className="text-gray-400 mb-6">
          {t("survey.voiceIntro")}
        </p>
        <div className="space-y-2">
          <Label className="text-gray-300">
            {t("survey.voiceQ")}
          </Label>
          <Textarea
            value={answers.club_improvement}
            onChange={(e) => handleAnswerChange("club_improvement", e.target.value)}
            placeholder="Share your thoughts..."
            className="bg-black/50 border-white/20 text-white focus:border-[#FBBF24] min-h-[150px] resize-none"
            data-testid="survey-open-text"
          />
          <p className="text-gray-500 text-sm">{t("survey.voiceNote")}</p>
        </div>
      </div>
    );
  }
}
